import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Image, Plus, Edit, Trash2, Save, X, RefreshCw, 
  ArrowUp, ArrowDown, Eye, EyeOff, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

function BannerForm({ banner, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    image_url: banner?.image_url || '',
    link: banner?.link || '',
    button_text: banner?.button_text || 'Shop Now',
    order: banner?.order || 0,
    is_active: banner?.is_active !== false
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.image_url) {
      toast.error('Image URL is required');
      return;
    }
    
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Card className="border-2 border-green-200 bg-green-50/50">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{banner ? 'Edit Banner' : 'New Banner'}</span>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Banner headline"
            />
          </div>
          <div className="space-y-2">
            <Label>Subtitle</Label>
            <Input
              value={formData.subtitle}
              onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
              placeholder="Supporting text"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Image URL *</Label>
          <Input
            value={formData.image_url}
            onChange={(e) => setFormData({...formData, image_url: e.target.value})}
            placeholder="https://example.com/banner.jpg"
          />
          {formData.image_url && (
            <div className="mt-2 rounded-lg overflow-hidden border">
              <img 
                src={formData.image_url} 
                alt="Preview" 
                className="w-full h-40 object-cover"
                onError={(e) => e.target.src = 'https://via.placeholder.com/800x300?text=Invalid+Image+URL'}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Link URL</Label>
            <Input
              value={formData.link}
              onChange={(e) => setFormData({...formData, link: e.target.value})}
              placeholder="/products or https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Button Text</Label>
            <Input
              value={formData.button_text}
              onChange={(e) => setFormData({...formData, button_text: e.target.value})}
              placeholder="Shop Now"
            />
          </div>
          <div className="space-y-2">
            <Label>Display Order</Label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
              placeholder="0"
            />
          </div>
        </div>

        <div className="flex items-center gap-4 pt-4">
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Banner
          </Button>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Active</span>
          </label>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BannerCard({ banner, onEdit, onDelete, onToggle }) {
  return (
    <Card className={`${!banner.is_active ? 'opacity-60' : ''}`}>
      <div className="relative">
        <img 
          src={banner.image_url || 'https://via.placeholder.com/800x300?text=No+Image'} 
          alt={banner.title}
          className="w-full h-40 object-cover rounded-t-lg"
        />
        <div className="absolute top-2 right-2 flex gap-1">
          <Badge variant={banner.is_active ? "default" : "secondary"}>
            {banner.is_active ? 'Active' : 'Inactive'}
          </Badge>
          <Badge variant="outline" className="bg-white">
            Order: {banner.order}
          </Badge>
        </div>
      </div>
      <CardContent className="pt-4">
        <h3 className="font-semibold text-lg">{banner.title || 'Untitled Banner'}</h3>
        <p className="text-sm text-gray-600 mb-3">{banner.subtitle || 'No subtitle'}</p>
        
        {banner.link && (
          <p className="text-xs text-blue-600 mb-3 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            {banner.link}
          </p>
        )}
        
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(banner)}>
            <Edit className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onToggle(banner)}
          >
            {banner.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {banner.is_active ? 'Hide' : 'Show'}
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="text-red-600 hover:bg-red-50"
            onClick={() => onDelete(banner)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/banners`);
      setBanners(response.data || []);
    } catch (error) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingBanner) {
        await axiosInstance.put(`${API_URL}/api/admin/banners/${editingBanner.id}`, formData);
        toast.success('Banner updated!');
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/banners`, formData);
        toast.success('Banner created!');
      }
      setShowForm(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to save banner');
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await axiosInstance.delete(`${API_URL}/api/admin/banners/${banner.id}`);
      toast.success('Banner deleted!');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggle = async (banner) => {
    try {
      await axiosInstance.put(`${API_URL}/api/admin/banners/${banner.id}`, {
        ...banner,
        is_active: !banner.is_active
      });
      toast.success(`Banner ${banner.is_active ? 'hidden' : 'activated'}!`);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Homepage Banners</h1>
          <p className="text-gray-600 mt-1">Manage carousel banners on the homepage</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setShowForm(true); setEditingBanner(null); }} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Add Banner
          </Button>
        )}
      </div>

      {showForm && (
        <BannerForm
          banner={editingBanner}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingBanner(null); }}
        />
      )}

      {banners.length === 0 ? (
        <Card className="p-8 text-center">
          <Image className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No banners yet</h3>
          <p className="text-gray-500 mb-4">Create your first homepage banner</p>
          <Button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Add Banner
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {banners.map((banner) => (
            <BannerCard
              key={banner.id}
              banner={banner}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
